import React, { useState } from 'react';
import { useBookingLinksQuery } from '../../lib/Queries';
import Loading from '../../components/Loading';
import PageHeading from '../../components/PageHeading';
import Select from '../../components/Select';
import { buildFormLink } from '../../lib/FormLinkBuilder';
import * as Toast from '../../lib/Toast';
import useBusinessNavigate, { getBusinessUrl } from '../../lib/useBusinessNavigate';
import CopyIcon from '../../components/icons/CopyIcon';
import FeatureIntro from '../../components/FeatureIntro';
import { LinkIcon } from '@heroicons/react/24/outline/index.js';

const BookingLinkRow = ({ link, selectedEventType, selectedBookableResource }) => {
    const navigate = useBusinessNavigate();

    // Build the direct link based on selected filters
    let displayUrl = link.url;
    const params = {};

    if (selectedEventType && link.eventTypeIds.includes(parseInt(selectedEventType))) {
        params.eventType = selectedEventType;
    }

    if (selectedBookableResource && link.bookableResourceIds.includes(parseInt(selectedBookableResource))) {
        params.bookableResource = selectedBookableResource;
    }

    if (Object.keys(params).length > 0) {
        displayUrl = buildFormLink(link.url, params);
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(displayUrl);
        Toast.success('Link Copied to Clipboard!');
    };

    return (
        <div className="py-4 pr-6 border-b last:border-b-0">
            <div className="flex flex-col items-start justify-between mb-3 gap-4">
                <div className="w-full">
                    <h3 className="text-xl font-semibold">{link.name}</h3>

                    <div className="flex items-center gap-x-6 text-gray-500 text-sm mb-4">
                        {link.bookingsThisMonth > 0 && (
                            <div className="flex items-center gap-x-2">
                                {link.location && <span>•</span>}
                                <span>{link.bookingsThisMonth} bookings this month</span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-x-2 bg-gray-50 rounded-full px-5 py-1">
                        <LinkIcon className="text-primary-700 w-5 h-5" />
                        <input
                            type="text"
                            value={displayUrl}
                            readOnly
                            className="flex-1 bg-transparent border-none text-gray-700 font-mono text-sm"
                            onFocus={(e) => e.target.select()}
                        />
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="text-primary-700 hover:text-primary-800"
                        >
                            <CopyIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <button
                        type="button"
                        className="btn-light-pill text-nowrap "
                        onClick={() => navigate(`/forms/${link.id}`)}
                    >
                        Edit Form
                    </button>

                    <button
                        type="button"
                        className="btn-light-pill text-nowrap"
                        onClick={() => navigate(`/forms/${link.id}/share`)}
                    >
                        Get Embed Code
                    </button>
                </div>

            </div>
        </div>
    );
};

const BookingLinksPage = () => {
    const { data, isLoading } = useBookingLinksQuery();
    const [selectedEventType, setSelectedEventType] = useState('0');
    const [selectedBookableResource, setSelectedBookableResource] = useState('0');

    if (isLoading) {
        return <Loading className="p-6" />;
    }

    const { bookingLinks = [], eventTypes = [], bookableResources = [] } = data || {};

    // Filter links based on selected filters
    let filteredLinks = bookingLinks;
    if (selectedEventType !== '0') {
        filteredLinks = filteredLinks.filter(link =>
            link.eventTypeIds.includes(parseInt(selectedEventType))
        );
    }
    if (selectedBookableResource !== '0') {
        filteredLinks = filteredLinks.filter(link =>
            link.bookableResourceIds.includes(parseInt(selectedBookableResource))
        );
    }

    const eventTypeOptions = [
        { value: '0', label: 'All Event Types' },
        ...eventTypes.map(et => ({ value: et.id.toString(), label: et.name }))
    ];

    const bookableResourceOptions = [
        { value: '0', label: 'All People/Things' },
        ...bookableResources.map(br => ({ value: br.id.toString(), label: br.name }))
    ];

    return (
        <div>
            <PageHeading heading="Booking Links" />

            <FeatureIntro>
                Booking links are your <span className="font-semibold">forms</span> that include a scheduling block.
                Use this section to manage every link clients can use to book time with you.
            </FeatureIntro>

            <div className="mb-6 max-w-2xl">
                <div className="panel-label">Filter By</div>
                <div className="flex items-center gap-x-4 mb-4">
                    <div className="flex flex-col gap-2 w-full sm:flex-row sm:gap-4 sm:w-auto">
                        <Select
                            options={eventTypeOptions}
                            value={selectedEventType}
                            onChange={setSelectedEventType}
                            inputClassName="w-full"
                        />
                        <Select
                            options={bookableResourceOptions}
                            value={selectedBookableResource}
                            onChange={setSelectedBookableResource}
                            inputClassName="w-full"
                        />
                    </div>
                    <div className="hidden ml-auto text-gray-500 rounded-full bg-gray-200 text-sm px-5 py-2.5 sm:block">
                        {filteredLinks.length} {filteredLinks.length === 1 ? 'Link' : 'Links'}
                    </div>
                </div>
            </div>

            <div className="max-w-2xl">
                <div className="panel-label">Booking Links</div>
                <div className="panel pl-6">
                    {filteredLinks.length === 0 && (
                        <div className="py-6 text-gray-500">No booking links found.</div>
                    )}
                    {filteredLinks.map(link => (
                        <BookingLinkRow
                            key={link.id}
                            link={link}
                            selectedEventType={selectedEventType !== '0' ? selectedEventType : null}
                            selectedBookableResource={selectedBookableResource !== '0' ? selectedBookableResource : null}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BookingLinksPage;
